import { Checkbox, Link } from '@arcadiapower/shrike';
import { useEffect, useState } from 'react';
import { Button } from '~/components/button';
import { COMMUNITY_SOLAR } from '~/components/dialogs/dialog-selector';
import { ErrorMessage } from '~/components/error-message';
import {
  type ImageType,
  TERMS_IMAGE,
  ImageSelector,
  UTILITY_CONNECT_IMAGE,
} from '~/components/image';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Separator } from '~/components/separator';
import { Description, Text, Title, Ul, UlListItem } from '~/components/typography';
import { useCopy } from '~/hooks';
import { LmiReviewContext } from '~/machines/lmi-review';
import { LmiStatus } from '~/machines/review-form';
import { LDFlags } from '~/providers';
import { LDClient } from '~/providers/launch-darkly';
import {
  Document,
  DocumentDetails,
} from '~/screens/lmi-proof-docs-upload/component/document-details.component';
import { LMIProofDocUploader } from '~/screens/lmi-proof-docs-upload/component/lmi-proof-doc-uploader.component';
import { LMIProofDoc } from '~/screens/lmi-proof-docs-upload/component/lmi-proof-docs-upload.component';
import { LMIProofDocSkipConfirmationDialog } from '~/screens/lmi-proof-docs-upload/lmi-proof-doc-skip-confirmation-dialog';
import { SelfAttestationData } from '~/screens/vnm-terms';
import { availableDocumentTypesPerUtility } from '~/utils/files';
import { mnDefaultCounties } from '../county-income-limits-mn';
import { SelfAttestation } from '../selfAttestation';

export const LmiReview = ({
  context,
  next,
  skip,
  overrideCopy,
  loading,
  pending,
  error,
}: LmiReviewProps) => {
  const copy = useCopy(context, checkCopy, overrideCopy);
  const enableManualProofCapture = LDClient.variation(LDFlags.enableManualProofCapture);

  const [selfAttestationData, setSelfAttestationData] = useState<
    Omit<SelfAttestationData, 'lmiProgram'>
  >({} as Omit<SelfAttestationData, 'lmiProgram'>);
  const [showSkipConfirmationDialog, setShowSkipConfirmationDialog] = useState(false);

  const [docs, setDocs] = useState<LMIProofDoc[] | Document[]>([]);
  const [documentTypes, setDocumentTypes] = useState<string[]>(['']);
  const [failure, setFailure] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [shouldBeDisabled, setShouldBeDisabled] = useState(true);
  const [lmiProofDocOptionsAvailableInLD, setLmiProofDocOptionsAvailableInLD] = useState(
    LDClient.variation(LDFlags.fetchUtilityLmiProofDocOptions),
  );
  const [lmiProofDocTypeOptions, setLmiProofDocTypeOptions] = useState(
    LDClient.variation(LDFlags.ffUtilityLmiProofDocsLabelOptions),
  );
  const [showSkipThisStepLink, setShowSkipThisStepLink] = useState(
    LDClient.variation(LDFlags.allowUserToSkipLmiProof),
  );
  const [documentTermsChecked, setDocumentTermsChecked] = useState(false);

  // eslint-disable-next-line camelcase
  const __EXPERIMENTAL_getDocumentTypes = (): string[] | Record<string, string> => {
    if (lmiProofDocTypeOptions && Object.keys(lmiProofDocTypeOptions).length > 0) {
      return lmiProofDocTypeOptions;
    } else {
      const localProofDocOptions =
        availableDocumentTypesPerUtility[context?.funnelPacket?.utility?.id] ||
        context.documentTypes;

      return lmiProofDocOptionsAvailableInLD || localProofDocOptions;
    }
  };

  const disableNextButton = () => {
    return enableManualProofCapture
      ? pending || !documentTermsChecked || submitDisabled || !shouldBeDisabled
      : pending || failure || docs.length === 0;
  };

  const getSelfAttestationSection = () => {
    return (
      <Container marginTop={6}>
        <SelfAttestation
          context={context}
          defaultCounties={mnDefaultCounties}
          onFormUpdate={setSelfAttestationData}
        />
      </Container>
    );
  };

  useEffect(() => {
    /**
     * if user reloads the page while in upload page, the component mounts
     * before the solar machine initialize and identify() with Launch Darkly.
     * to prevent the component from using stale data, we add a listener to
     * update the needed LD values 'onChange' below.
     */
    LDClient.on('change', () => {
      setShowSkipThisStepLink(LDClient.variation(LDFlags.allowUserToSkipLmiProof));
    });
  }, []);

  useEffect(() => {
    /**
     *  Same as above, needed for correct proof docs to be displayed
     *  when user reloads the page while in upload page, on change is accepting
     *  only 1 flag at a time, so we need to add a listener for each flag
     */
    LDClient.on('change', () => {
      setLmiProofDocOptionsAvailableInLD(
        LDClient.variation(LDFlags.fetchUtilityLmiProofDocOptions),
      );
      setLmiProofDocTypeOptions(LDClient.variation(LDFlags.ffUtilityLmiProofDocsLabelOptions));
    });
  }, []);

  useEffect(() => {
    const seltAttestationNotFilled = selfAttestationData
      ? Object.keys(selfAttestationData)
          .filter(key => key !== 'lmiProgram')
          .some(value => selfAttestationData[value] === '')
      : false;
    if (!failure && !seltAttestationNotFilled && docs.length > 0) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  }, [docs, failure, selfAttestationData]);

  return (
    <TwoColumnGrid testId="screen-Review-Form">
      <TwoColumnGrid.Primary>
        <Title fontWeight={700} fontSize="24px">
          {copy.title}
        </Title>
        {copy.defaultDescriptionLineOne && (
          <Description mb={4}>{copy.defaultDescriptionLineOne}</Description>
        )}
        {copy.defaultDescriptionLineTwo && (
          <Description mb={4}>{copy.defaultDescriptionLineTwo}</Description>
        )}
        {enableManualProofCapture ? (
          <>
            <Description mb={4}>{copy.docsInfo}</Description>
            <Ul mb={4}>
              {copy.infoList.map(text => (
                <UlListItem key={text}>{text}</UlListItem>
              ))}
            </Ul>
            <Description mb={4}>{copy.missingDocumentInfo}</Description>
          </>
        ) : null}
        {getSelfAttestationSection()}
        {!enableManualProofCapture ? (
          <LMIProofDocUploader
            documentTypes={documentTypes}
            docs={docs as LMIProofDoc[]}
            pending={pending}
            allDocumentTypes={__EXPERIMENTAL_getDocumentTypes()}
            onDocsChange={setDocs}
            onDocumentTypesChange={setDocumentTypes}
            onFailureChange={setFailure}
          />
        ) : (
          <DocumentDetails
            allDocumentTypes={__EXPERIMENTAL_getDocumentTypes()}
            pending={pending}
            setShouldBeDisabled={setShouldBeDisabled}
            setDocs={setDocs}
          />
        )}
        {error ? <ErrorMessage error={error} /> : null}
        <Separator pt={4} pb={4} />
        {enableManualProofCapture && (
          <Checkbox
            margin="16px 0 16px 0"
            checked={documentTermsChecked}
            onChange={e => setDocumentTermsChecked(e.target.checked)}
          >
            <Text textStyle="paragraph400" color="secondary">
              {copy.documentTerms}
            </Text>
          </Checkbox>
        )}
        <Button
          disabled={disableNextButton()}
          mb={4}
          mode="primary"
          testId="review-form-button"
          onClick={() => {
            next({
              SelfAttestationData: {
                ...selfAttestationData,
                lmiProgram: lmiProofDocTypeOptions[(docs[0] as LMIProofDoc).documentType],
              },
              lmiStatus: 'isLmi',
              proofDocs: docs,
            });
          }}
          loading={loading}
        >
          {copy.next}
        </Button>
        {showSkipThisStepLink ? (
          <Container alignSelf="center">
            {loading ? null : (
              <Link
                color="primary"
                type="standalone"
                textAlign="center"
                onClick={e => {
                  e.stopPropagation();
                  if (pending) return;
                  if (docs.length) {
                    setShowSkipConfirmationDialog(true);
                    return;
                  }
                  skip({
                    SelfAttestationData: null,
                    lmiStatus: 'notLmi',
                    proofDocs: [],
                  });
                }}
                openInNewTab={false}
              >
                {copy.skip}
              </Link>
            )}
          </Container>
        ) : null}
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
        {showSkipConfirmationDialog && (
          <LMIProofDocSkipConfirmationDialog
            onSkip={() => {
              setShowSkipConfirmationDialog(false);
              setDocs([]);
              skip({
                SelfAttestationData: null,
                lmiStatus: 'notLmi',
                proofDocs: [],
              });
            }}
            onCancel={() => setShowSkipConfirmationDialog(false)}
          />
        )}
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const checkCopy = () => {
  return defaultCopy();
};

export const defaultCopy = () => {
  return {
    title: 'Complete your income-based eligibility form',
    defaultDescriptionLineOne:
      'To determine whether you are eligible for a 10% savings rate, we’ll need to verify whether you qualify as a low-to-moderate (LMI) income household. (All other subscribers receive 5% savings on their solar credits.) To qualify, you must participate in an approved income-eligible program and you must attest that your household’s annual income is below a certain limit based on your county and household occupancy.',
    defaultDescriptionLineTwo:
      'First, select your county, household occupancy, and income-eligible program. Then upload a photo of a document that proves your active participation in one of the qualifying programs.',
    docsInfo: 'Your document must include the following information:',
    infoList: ['Qualifying program name', 'Participant name', 'Issue date or expiration date'],
    missingDocumentInfo:
      'If your document is missing any of this information, please upload an additional document that includes the missing information. If you upload an additional document, ensure that either the participant name or an ID number are present on both documents.',
    imageType: TERMS_IMAGE as ImageType,
    next: 'Review form',
    skip: 'Skip this step',
    documentTerms:
      'By checking this box, I hereby affirm that the documentation I have provided is true and accurate to the best of my knowledge, is currently effective and/or has been effective within the past twelve (12) months, and corresponds with my own enrollment and participation, or the enrollment and participation of another member of my household, in the income-qualifying assistance program identified therein.',
    nonLmiNext: 'Continue',
    descriptionLineOneSponsoredSolar:
      'To find out if you are eligible for additional savings, please fill out an income status verification form. ',
    descriptionLineOneNJSelfAttestation:
      'To find out if you are eligible for additional savings, your state needs you to fill out an income status verification form.',
    descriptionLineTwoNJSelfAttestation:
      "First, select your county, household occupancy, and low-to-moderate income qualifying program (if applicable). Then click 'Review form' and you will be taken to a new page that requires your confirmation.",
    descriptionLineOneMDSelfAttestation:
      'Households qualify as low and moderate income based on criteria specified by program rules',
    descriptionLineTwo: '',
    descriptionLineOne: '',
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    isLmi: {
      primary: 'Yes, I qualify as LMI',
      secondary:
        'If you choose this option, after clicking "Looks good to me" below, you will see a form to self-attest your income status.',
    },
    maybeLmi: {
      primary: "I don't know if I qualify as LMI",
      secondary:
        'If you aren\'t sure, that\'s ok. After clicking "Looks good to me" below, you will see a form to attest your income status. Go ahead and sign it. We will review your income level and check whether you qualify as LMI.',
    },
    notLmi: {
      primary: 'No, I do not qualify as LMI',
      secondary: "You're still guaranteed to save with community solar!",
    },
    lmiProofDocs: {
      description:
        "Participation in select assistance programs can help you get enrolled on a solar farm faster. Upload your document (supported formats: PNG, JPEG, HEIC) below. We'll review your documentation and once approved you'll be on your way to solar savings.",
      learnMoreModal: COMMUNITY_SOLAR,
      imageType: UTILITY_CONNECT_IMAGE as ImageType,
      saveContinue: 'Save and continue',
      skip: 'Skip this step',
      title: 'Add your eligibility documents',
      step: 'Step 3 of 4',
    },
  };
};
export type LmiReviewCopy = ReturnType<typeof defaultCopy>;

export interface LmiReviewProps {
  context: LmiReviewContext;
  loading: boolean;
  skip: (data: {
    SelfAttestationData: SelfAttestationData | null;
    lmiStatus: LmiStatus;
    proofDocs: LMIProofDoc[] | Document[];
  }) => void;
  pending: boolean;
  next: (data: {
    SelfAttestationData: SelfAttestationData | null;
    lmiStatus: LmiStatus;
    proofDocs: LMIProofDoc[] | Document[];
  }) => void;
  error?: string;
  overrideCopy?: (context: LmiReviewContext) => Partial<ReturnType<typeof defaultCopy>>;
}
